import {
  InternalPayItemConnectionClassType,
  alphabeticalCompare,
} from '@cims-online/shared'
import {
  PayItemConnectionGroupsQuery,
  PayItemConnectionGroupsQueryResult,
  usePayItemConnectionGroupsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import {
  LabeledValueType,
  DefaultOptionType,
} from 'components/CIMSSelect/types'
import { useCallback } from 'react'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { useProjectContext } from 'context/ProjectContext'

export type PayItemConnectionGroup =
  PayItemConnectionGroupsQuery['project']['payItemConnectionGroups'][0]

type Connection =
  PayItemConnectionGroupsQuery['project']['payItemConnections'][number]

type GroupsContext = Pick<PayItemConnectionGroupsQueryResult, 'refetch'> & {
  groups: PayItemConnectionGroup[] | undefined
  fetching: PayItemConnectionGroupsQueryResult['loading']
  connectionOptions: DefaultOptionType[]
  toConnectionValues: (options: {
    connections: Connection[]
    internalIds: string[]
  }) => LabeledValueType[]
  fromConnectionValues: (values: LabeledValueType[]) => {
    connectionIds: string[]
    internalConnectionIds: string[]
  }
}

const { Provider, useContextFn } =
  createStrictContext<GroupsContext>('GroupsContext')

export const useGroupsContext = useContextFn

const GroupsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    data,
    loading: fetching,
    refetch,
  } = usePayItemConnectionGroupsQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  })
  const connectionLabel = ({ name, googleSheet }: Connection) =>
    name ??
    (googleSheet ? googleSheet?.title ?? googleSheet.compositeId : 'Unknown')
  const connections = useOptions({
    items: data?.project?.payItemConnections,
    value: ({ id, __typename }) => `${__typename}_${id}`,
    label: connectionLabel,
    tooltip: ({ googleSheet }) => (googleSheet ? 'Google Sheet' : undefined),
    sort: (a, b) => {
      const A = connectionLabel(a)
      const B = connectionLabel(b)
      return alphabeticalCompare(A, B)
    },
  })
  const internals = useOptions({
    items: InternalPayItemConnectionClassType.values,
    value: ({ id }) => `internal_${id}`,
    label: ({ module }) => module.name,
    sort: (a, b) => alphabeticalCompare(a.module, b.module, 'name'),
  })
  const toConnectionValues = useCallback<GroupsContext['toConnectionValues']>(
    ({ connections, internalIds }) =>
      toLabeledValues([...connections, ...internalIds], {
        type: 'callback',
        callback: (item) => {
          if (typeof item === 'string')
            return {
              value: `internal_${item}`,
              label:
                InternalPayItemConnectionClassType.fromIdOrFail(item).module
                  .name,
              tooltip: 'Module',
            }
          const { id, __typename, googleSheet, name } = item
          return {
            value: `${__typename}_${id}`,
            label:
              name ??
              (googleSheet
                ? googleSheet?.title ?? googleSheet.compositeId
                : 'Unknown'),
            tooltip: googleSheet ? 'Google Sheet' : undefined,
          }
        },
      }),
    []
  )
  const fromConnectionValues = useCallback<
    GroupsContext['fromConnectionValues']
  >((values) => {
    return values.reduce<ReturnType<GroupsContext['fromConnectionValues']>>(
      (map, { value }) => {
        const [typename, id] = value.toString().split('_')
        if (typename === 'internal') map.internalConnectionIds.push(id)
        else map.connectionIds.push(id)
        return map
      },
      { connectionIds: [], internalConnectionIds: [] }
    )
  }, [])
  return (
    <Provider
      value={{
        groups: data?.project?.payItemConnectionGroups,
        fetching,
        refetch,
        connectionOptions: [
          { value: 'connections', label: 'Connections', options: connections },
          { value: 'internals', label: 'Modules', options: internals },
        ],
        toConnectionValues,
        fromConnectionValues,
      }}
    >
      {children}
    </Provider>
  )
}

export default GroupsProvider
